import { useCallback, useEffect, useRef } from 'react';
import { useUnleashClient, useUnleashContext } from '@unleash/proxy-client-react';

import { useRuntimeContext } from '../runtime';
import { buildUnleashContext, getToolbar, syncContextToToolbar } from './feature-flags.helpers';

/**
 * Syncs the full runtime context to the Unleash SDK and toolbar. Runs whenever the
 * runtime context changes (e.g., after auth), and re-injects after toolbar context resets.
 *
 * This hook is internal to the feature-flags package; it is called by the provider.
 */
export function useRuntimeContextSync(): void {
    const runtimeContext = useRuntimeContext();
    const unleashClient = useUnleashClient();
    const updateUnleashContext = useUnleashContext();
    const unsubscribeRef = useRef<(() => void) | null>(null);

    /**
     * Pushes the current runtime context to the SDK and toolbar.
     */
    const injectContext = useCallback(() => {
        syncContextToToolbar(unleashClient, updateUnleashContext, buildUnleashContext(runtimeContext));
    }, [runtimeContext, unleashClient, updateUnleashContext]);

    // Inject whenever runtime context changes
    useEffect(() => {
        injectContext();
    }, [injectContext]);

    // Re-inject after toolbar "Reset all context"
    useEffect(() => {
        const toolbar = getToolbar(unleashClient);

        if (!toolbar) {
            return;
        }

        if (unsubscribeRef.current) {
            unsubscribeRef.current();
        }

        unsubscribeRef.current = toolbar.stateManager.subscribe((event) => {
            if (event.type !== 'context_override_changed') {
                return;
            }

            const overrides = toolbar.getState().contextOverrides;
            const isReset = Object.keys(overrides).length === 0;

            if (isReset) {
                setTimeout(() => {
                    injectContext();
                }, 50);
            }
        });

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [unleashClient, injectContext]);
}
